import { Link, Switch } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { cloneDeep, isEmpty } from 'lodash-es';
import React, { JSX, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IS3AccessPoint } from '~/src/dash/api/s3-access-points';
import { SimpleSingleSelect } from '~/src/dash/components/dropdowns/SimpleSingleSelect';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { spacingV2 } from '~/src/dash/components/layout/common';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import { getLineHeight } from '~/src/dash/components/text/common';
import {
  Col,
  Cols,
  ISimpleOption,
  ISimpleOptionSelect,
  Row,
  Rows,
  Text,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { extractRouteParams, getRoutePath, RoutePublisherSettings } from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { isTopTierPublisher } from '~/src/dash/utils/ui';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  deliveries,
  EReportDelivery,
  EReportFrequency,
  EReportPeriod,
  frequencies,
  getReportDeliveryTKey,
  getReportFrequencyTKey,
  getReportPeriodTKey,
  iasPeriods,
  monthlyScheduledDays,
  periods,
  weeklyScheduledDays,
} from '~/src/dash/views/reporting/common';
import { Recipients } from '~/src/dash/views/reporting/ReportBuilder/Schedule/Recipients';

interface IErrors {
  delivery: string | JSX.Element;
  frequency: string | JSX.Element;
  period: string | JSX.Element;
  scheduledDay: string | JSX.Element;
}

export interface IScheduleConfig {
  delivery: string;
  frequency: string;
  isSchedulingActive: boolean;
  period: string;
  recipients: Array<string>;
}

interface IScheduleSectionProps {
  delivery: string;
  frequency: string;
  isIasReport?: boolean;
  isLoading: boolean;
  isSchedulingActive: boolean;
  period: string;
  recipients: Array<string>;
  s3AccessPoint: IS3AccessPoint | null;
  scheduledDay: string;
  subject: string;
  setDelivery: (delivery: string) => void;
  setEmailSubject: (subject: string) => void;
  setFrequency: (frequency: string) => void;
  setIsSchedulingActive: (active: boolean) => void;
  setPeriod: (period: string) => void;
  setRecipients: (recipients: string[]) => void;
  setScheduledDay: (scheduledDay: string) => void;
}

export const ScheduleSection = ({
  delivery,
  frequency,
  isIasReport,
  isLoading,
  isSchedulingActive,
  period,
  recipients,
  s3AccessPoint,
  scheduledDay,
  subject,
  setDelivery,
  setEmailSubject,
  setFrequency,
  setIsSchedulingActive,
  setPeriod,
  setRecipients,
  setScheduledDay,
}: IScheduleSectionProps) => {
  const defaultError: IErrors = {
    delivery: '',
    frequency: '',
    period: '',
    scheduledDay: '',
  };

  const { t } = usePublicaTranslation();
  const { session, user } = useBaseStore();

  const [errors, setErrors] = useState<IErrors>(defaultError);

  let filteredPeriods = periods;

  // Deletes period "Current Year" if publisher is not tier 1 / platinum
  if (!isTopTierPublisher(session.publisher.ID)) {
    filteredPeriods = periods.filter(
      (period) => period.value !== String(EReportPeriod.CurrentYear),
    );
  }

  // validate the schedule setting
  const isScheduleSettingValid = () => {
    const newErrors: IErrors = cloneDeep(defaultError);
    // Cannot schedule year-to-date report on a daily interval
    if (
      Number(period) === EReportPeriod.CurrentYear &&
      Number(frequency) === EReportFrequency.FrequencyDaily
    ) {
      newErrors.period = t('ReportingUnableToScheduleYearToDateReport');
    }

    // cannot schedule for AWS S3 if publisher is missing S3 access point credentials
    if (Number(delivery) === EReportDelivery.AWSS3 && isEmpty(s3AccessPoint)) {
      newErrors.delivery = (
        <Link
          target='_blank'
          style={{
            textDecoration: 'underline',
            color: publicaColors.orange,
            display: 'inline-block',
          }}
          href={
            '#' +
            getRoutePath(
              RoutePublisherSettings,
              extractRouteParams({
                session,
                user,
              }),
            )
          }
        >
          <Text tKey='ReportingInputAccessPointCredentials' small color={publicaColors.orange} />
        </Link>
      );
    }

    return isSchedulingActive ? newErrors : defaultError;
  };

  // helper to format options for SimpleSingleSelect
  const getOptions = (
    items: Array<ISimpleOption>,
    selectedValue: string,
    isOptionValid: (value: ISimpleOption) => boolean = () => false,
  ): Array<ISimpleOptionSelect> => {
    return items.map((item) => ({
      ...item,
      isSelected: item.value === selectedValue,
      isDisabled: !isOptionValid(item),
    }));
  };

  const onFrequencyChange = (value: string) => {
    unstable_batchedUpdates(() => {
      setFrequency(value);
      setScheduledDay('1');
    });
  };

  // isFrequencyOptionValid & isPeriodOptionValid
  // help to invalid scheduling year-to-date report on a daily/weekly interval
  const isFrequencyOptionValid = (frequencyOption: ISimpleOption): boolean => {
    if (
      Number(period) === EReportPeriod.CurrentYear &&
      (Number(frequencyOption.value) === EReportFrequency.FrequencyDaily ||
        Number(frequencyOption.value) === EReportFrequency.FrequencyWeekly)
    ) {
      return false;
    }
    return true;
  };

  const isPeriodOptionValid = (periodOption: ISimpleOption): boolean => {
    if (
      Number(periodOption.value) === EReportPeriod.CurrentYear &&
      (Number(frequency) === EReportFrequency.FrequencyDaily ||
        Number(frequency) === EReportFrequency.FrequencyWeekly)
    ) {
      return false;
    }
    return true;
  };

  const getScheduledDayOptions = (frequency: string): ISimpleOption[] => {
    return frequency === String(EReportFrequency.FrequencyMonthly)
      ? monthlyScheduledDays
      : weeklyScheduledDays.map((day) => ({
          ...day,
          label: t(day.label) || day.label,
        }));
  };

  const getFrequencyOptions = (frequencies: ISimpleOption[]): ISimpleOption[] => {
    return frequencies.map((frequency) => ({
      ...frequency,
      label: t(getReportFrequencyTKey(Number(frequency.value))) || frequency.label,
    }));
  };

  const getPeriodOptions = (periods: ISimpleOption[]): ISimpleOption[] => {
    return periods.map((period) => ({
      ...period,
      label: t(getReportPeriodTKey(Number(period.value))) || period.label,
    }));
  };

  const getDeliveryOptions = (deliveries: ISimpleOption[]): ISimpleOption[] => {
    return deliveries.map((delivery) => ({
      ...delivery,
      label: t(getReportDeliveryTKey(Number(delivery.value))) || delivery.label,
    }));
  };

  // handle change whenever the schedule setting updates
  useEffect(() => {
    const errors = isScheduleSettingValid();
    setErrors(errors);
  }, [isSchedulingActive, period, frequency, delivery, recipients, scheduledDay]);

  return !isLoading ? (
    <Rows>
      <Row>
        <Cols gap={spacingV2.medium} style={{ alignItems: 'center' }}>
          <Col displayFlex style={{ alignItems: 'center' }}>
            <IconV2 name='schedule' large />
          </Col>
          <Col>
            <Text tKey='Schedule' sizeV2 bold large />
          </Col>
        </Cols>
      </Row>

      <Rows extraLargeHorizontalPaddingV2>
        <Row noVerticalPadding marginBottom={spacingV2.small}>
          <Cols style={{ alignItems: 'center' }}>
            <Col style={{ minWidth: '7rem' }}>
              <Text tKey='Scheduled' bold small />
            </Col>
            <Col style={{ marginRight: spacingV2.medium }}>
              <Switch
                checked={isSchedulingActive}
                onChange={() => {
                  setIsSchedulingActive(!isSchedulingActive);
                }}
                name='Status'
              />
            </Col>
            <Col>
              <Text
                tKey={isSchedulingActive ? 'Active' : 'Inactive'}
                small
                bold
                muted={!isSchedulingActive}
              />
            </Col>
          </Cols>
        </Row>

        <Row noVerticalPadding marginBottom={spacingV2.medium}>
          <Cols>
            <Col style={{ minWidth: '7rem', marginTop: spacingV2.extraSmall }}>
              <Text tKey='SubjectName' bold small muted={!isSchedulingActive} />
            </Col>
            <Col style={{ marginRight: spacingV2.medium }}>
              <TextInput
                initialValue={subject}
                isDisabled={!isSchedulingActive}
                width='22rem'
                inputSize='small'
                placeholder={t('ReportingEmailSubjectName')}
                handleOnChange={(input) => setEmailSubject(input)}
              />
              <Text
                tKey='ReportingSubjectNameDescription'
                extraSmall
                muted
                style={{ marginTop: spacingV2.extraSmall }}
              />
            </Col>
          </Cols>
        </Row>

        <Row noVerticalPadding>
          <Cols wrap style={{ alignItems: 'center' }}>
            <Col marginRight={spacingV2.huge} marginBottom={spacingV2.medium}>
              <Cols style={{ alignItems: 'center' }}>
                <Col style={{ minWidth: '7rem' }}>
                  <Text tKey='Frequency' bold small muted={!isSchedulingActive} />
                </Col>
                <Col>
                  <SimpleSingleSelect
                    isDisabled={!isSchedulingActive}
                    inputWidth='12.5rem'
                    onChange={(id) => onFrequencyChange(id)}
                    options={getOptions(
                      getFrequencyOptions(frequencies),
                      frequency,
                      isFrequencyOptionValid,
                    )}
                    popoverWidth='12.5rem'
                  />
                </Col>
              </Cols>
            </Col>

            {/* TODO: put back in if we add Scheduled Dates functionality */}
            {/* <Col marginBottom={spacingV2.medium}>
              <Cols style={{ alignItems: 'center' }}>
                <Col>
                  <Cols style={{ alignItems: 'center' }}>
                    <Col style={{ minWidth: '7rem' }}>
                      <Text small bold muted={!isSchedulingActive}>
                        Start - End Date
                      </Text>
                    </Col>
                    <Col>
                      <CalendarV2
                        isDisabled={!isSchedulingActive}
                        minDate={today}
                        calStartDate={dateRange ? dateRange[0] : today}
                        calEndDate={dateRange ? dateRange[1] : today}
                        handleApplyDatesChange={({ newStartDate, newEndDate }) =>
                          setScheduledDateRange([newStartDate, newEndDate])
                        }
                        uniqueID='reportScheduleCal'
                        openDirection='up'
                      />
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Col> */}
          </Cols>

          {errors.frequency && (
            <Cols style={{ alignItems: 'center' }}>
              <Col style={{ minWidth: '7rem' }} />
              <Col>
                <Text small color={publicaColors.orange} style={{ marginTop: spacingV2.small }}>
                  {errors.frequency}
                </Text>
              </Col>
            </Cols>
          )}
        </Row>

        {[
          String(EReportFrequency.FrequencyMonthly),
          String(EReportFrequency.FrequencyWeekly),
        ].includes(frequency) && (
          <Row noVerticalPadding marginBottom={spacingV2.medium}>
            <Cols style={{ alignItems: 'center' }}>
              <Col style={{ minWidth: '7rem' }}>
                <Text tKey='ScheduledDay' bold small muted={!isSchedulingActive} />
              </Col>
              <Col displayFlex>
                <SimpleSingleSelect
                  isDisabled={!isSchedulingActive}
                  inputWidth='12.5rem'
                  popoverWidth='12.5rem'
                  options={getOptions(getScheduledDayOptions(frequency), scheduledDay, () => true)}
                  onChange={(value) => setScheduledDay(value)}
                />
              </Col>
            </Cols>

            {errors.scheduledDay && (
              <Cols style={{ alignItems: 'center' }}>
                <Col style={{ minWidth: '7rem' }} />
                <Col>
                  <Text small color={publicaColors.orange} style={{ marginTop: spacingV2.small }}>
                    {errors.scheduledDay}
                  </Text>
                </Col>
              </Cols>
            )}
          </Row>
        )}

        <Row noVerticalPadding marginBottom={spacingV2.medium}>
          <Cols>
            <Col style={{ minWidth: '7rem', marginTop: spacingV2.extraSmall }}>
              <Text tKey='PeriodRange' bold small muted={!isSchedulingActive} />
            </Col>
            <Col>
              <SimpleSingleSelect
                isDisabled={!isSchedulingActive}
                inputWidth='12.5rem'
                options={getOptions(
                  getPeriodOptions(isIasReport ? iasPeriods : filteredPeriods),
                  period,
                  isPeriodOptionValid,
                )}
                onChange={(id) => setPeriod(id)}
                popoverWidth='12.5rem'
              />
              <Text
                tKey='ReportingPeriodRangeDescription'
                extraSmall
                muted
                style={{ marginTop: spacingV2.extraSmall }}
              />
            </Col>
          </Cols>

          {errors.period && (
            <Cols style={{ alignItems: 'center' }}>
              <Col style={{ minWidth: '7rem' }} />
              <Col>
                <Text small color={publicaColors.orange} style={{ marginTop: spacingV2.small }}>
                  {errors.period}
                </Text>
              </Col>
            </Cols>
          )}
        </Row>

        <Row noVerticalPadding marginBottom={spacingV2.medium}>
          <Cols style={{ alignItems: 'center' }}>
            <Col style={{ minWidth: '7rem' }}>
              <Text tKey='Delivery' bold small muted={!isSchedulingActive} />
            </Col>
            <Col displayFlex>
              <PillSwitcherV2
                size='small'
                isDisabled={!isSchedulingActive}
                options={getOptions(getDeliveryOptions(deliveries), delivery)}
                onChange={(value) => setDelivery(value)}
              />
            </Col>
          </Cols>

          {errors.delivery && (
            <Cols style={{ alignItems: 'center' }}>
              <Col style={{ minWidth: '7rem' }} />
              <Col>
                <Text small color={publicaColors.orange} style={{ marginTop: spacingV2.small }}>
                  {errors.delivery}
                </Text>
              </Col>
            </Cols>
          )}
        </Row>

        <Row noVerticalPadding>
          <Recipients
            recipients={recipients}
            onChange={setRecipients}
            isDisabled={!isSchedulingActive || Number(delivery) === EReportDelivery.AWSS3}
          />
        </Row>
      </Rows>
    </Rows>
  ) : (
    <Skeleton variant='text' animation='wave' width='100%' height={getLineHeight('extraLarge')} />
  );
};
